
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, FileText, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/layout/PageHeader';

export interface StorePage {
  id: string;
  title: string;
  slug: string;
  content: string | null;
  templateSuffix: string | null;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function Pages() {
  const navigate = useNavigate();
  
  const queryClient = useQueryClient();

  const { data: pages, isLoading } = useQuery({
    queryKey: ['store-pages'],
    queryFn: async () => {
      const res: any = await apiClient.get<{ data: StorePage[] }>('/merchant/pages');
      return res;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/merchant/pages/${id}`);
    },
    onSuccess: () => {
      toast('Page deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['store-pages'] });
    },
    onError: () => {
      toast('Failed to delete page');
    },
  });

  if (isLoading) {
    return <div className="flex justify-center p-12">Loading pages...</div>;
  }

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      <div className="flex items-center justify-between">
        <PageHeader 
          title="Pages" 
          description="Manage your custom online store pages."
        />
        <Button onClick={() => navigate('/store/pages/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Add Page
        </Button>
      </div>

      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!pages?.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No pages found. Create your first page to get started.
                </TableCell>
              </TableRow>
            ) : (
              pages.map((page: any) => (
                <TableRow key={page.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      {page.title}
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
                      /pages/{page.slug}
                    </code>
                  </TableCell>
                  <TableCell>
                    {page.templateSuffix || 'page'}
                  </TableCell>
                  <TableCell>
                    {page.isPublished ? (
                      <Badge variant="default" className="bg-green-500 hover:bg-green-600">
                        <CheckCircle2 className="mr-1 h-3 w-3" /> Published
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <XCircle className="mr-1 h-3 w-3" /> Draft
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(`/store/pages/${page.id}`)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this page?')) {
                          deleteMutation.mutate(page.id);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
